// Pure logic for the dashboard "My Todos" widget. Dates are LOCAL calendar days
// ("YYYY-MM-DD"), never routed through UTC. The week starts on Sunday, matching
// the Leave > Calendar page. Shared date helpers come from attendance-calendar.

import {
  addDays, addMonths, endOfMonth, isoDate, parseIso, startOfMonth,
  type PeriodMode, type Range,
} from "./attendance-calendar.js";
import { buildDayView, type CalendarTodo, type DayTodoView } from "../../../lib/calendarTodos.js";

export { isoDate, parseIso, addDays, relativeLabel, weekLabel, monthLabel, dayLabel, dayLongLabel } from "./attendance-calendar.js";
export type { PeriodMode, Range } from "./attendance-calendar.js";

export const WEEK_SLOTS = 4;
export const MONTH_MINI = 3;

/** The Sunday on or before `d`. */
export function startOfWeekSun(d: Date): Date {
  return addDays(d, -d.getDay());
}

export function rangeFor(anchor: Date, mode: PeriodMode): Range {
  if (mode === "week") {
    const start = startOfWeekSun(anchor);
    return { from: isoDate(start), to: isoDate(addDays(start, 6)) };
  }
  return { from: isoDate(startOfMonth(anchor)), to: isoDate(endOfMonth(anchor)) };
}

export function stepPeriod(anchor: Date, mode: PeriodMode, delta: number): Date {
  return mode === "week" ? addDays(anchor, delta * 7) : addMonths(anchor, delta);
}

/** Whole periods between two anchors: 0 = same, 1 = one in the past, -1 = next. */
export function periodsAgo(anchor: Date, mode: PeriodMode, today: Date): number {
  if (mode === "week") {
    const a = startOfWeekSun(anchor), b = startOfWeekSun(today);
    // Date.UTC on the Y/M/D parts: immune to DST making a "week" 167 or 169 hours.
    const ua = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
    const ub = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
    return Math.round((ub - ua) / (7 * 24 * 60 * 60 * 1000));
  }
  return (today.getFullYear() - anchor.getFullYear()) * 12 + (today.getMonth() - anchor.getMonth());
}

/** Sunday-first blank cells before the 1st in the month grid. */
export function leadingBlanksSun(anchor: Date): number {
  return startOfMonth(anchor).getDay();
}

/** Number of week rows the month grid needs (4-6). */
export function monthRowCount(anchor: Date): number {
  return Math.ceil((leadingBlanksSun(anchor) + endOfMonth(anchor).getDate()) / 7);
}

/** Sun..Sat headers in the viewer's locale. */
export function weekdayNamesSun(locale?: string): string[] {
  // 2024-01-07 was a Sunday.
  return Array.from({ length: 7 }, (_, i) => new Date(2024, 0, 7 + i).toLocaleDateString(locale, { weekday: "short" }));
}

/** Previous/next period ranges, for the idle prefetch. */
export function neighbourRanges(anchor: Date, mode: PeriodMode): Array<[string, string]> {
  return [-1, 1].map(d => { const r = rangeFor(stepPeriod(anchor, mode, d), mode); return [r.from, r.to] as [string, string]; });
}

/**
 * Selection after navigating: keep it when still inside the new period, else
 * today when today is in view, else the first day of the period.
 */
export function selectionAfterNav(selected: string, range: Range, todayIso: string): string {
  if (selected >= range.from && selected <= range.to) return selected;
  return todayIso >= range.from && todayIso <= range.to ? todayIso : range.from;
}

/** Default selection: today when the current period is in view, else the period's first day. */
export function defaultSelection(range: Range, todayIso: string): string {
  return todayIso >= range.from && todayIso <= range.to ? todayIso : range.from;
}

export interface PeriodCounts { open: number; done: number; overdue: number }

/** Counts over the visible period only: OPEN, DONE and OVERDUE (open before today). */
export function countsFor(todos: CalendarTodo[], range: Range, todayIso: string): PeriodCounts {
  let open = 0, done = 0, overdue = 0;
  for (const t of todos) {
    const k = t.date.split("T")[0]!;
    if (k < range.from || k > range.to) continue;
    if (t.done) done++;
    else { open++; if (k < todayIso) overdue++; }
  }
  return { open, done, overdue };
}

export interface WeekTileView {
  view: DayTodoView;
  /** Circles to draw: up to 4, or 3 plus a "+N" when there are more than 4. */
  circles: CalendarTodo[];
  plusN: number;
  /** "2/5", "N late" or an en dash. */
  line: { kind: "ratio" | "late" | "none"; text: string };
}

export function weekTileView(todos: CalendarTodo[], date: string, todayIso: string): WeekTileView {
  const view = buildDayView(todos, date, todayIso, todos.length);
  const circles = view.total > WEEK_SLOTS ? view.sorted.slice(0, WEEK_SLOTS - 1) : view.sorted;
  const plusN = view.total > WEEK_SLOTS ? view.total - (WEEK_SLOTS - 1) : 0;
  const line: WeekTileView["line"] = view.total === 0
    ? { kind: "none", text: "–" }
    : view.overdue > 0 ? { kind: "late", text: `${view.overdue} late` } : { kind: "ratio", text: `${view.doneCount}/${view.total}` };
  return { view, circles, plusN, line };
}

/** Quick-add validation: single line, trimmed, 1-120 chars; null when it should not be created. */
export function cleanQuickAddTitle(raw: string): string | null {
  const t = raw.replace(/\s+/g, " ").trim();
  return t.length >= 1 && t.length <= 120 ? t : null;
}

export function calendarLink(date: string, todoId?: string): string {
  return `/leave?date=${date}${todoId ? `&todo=${encodeURIComponent(todoId)}` : ""}`;
}

/** Milliseconds until the next local midnight (+1s so the new day is surely in effect). */
export function msUntilMidnight(now = new Date()): number {
  const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 1);
  return next.getTime() - now.getTime();
}

/** Tooltip for a tile or cell: "Mon 2 Nov — A, B, C…" or "no todos". */
export function dayTooltip(date: string, sorted: CalendarTodo[], locale?: string): string {
  const d = parseIso(date);
  const head = `${d.toLocaleDateString(locale, { weekday: "short" })} ${d.getDate()} ${d.toLocaleDateString(locale, { month: "short" })}`;
  if (sorted.length === 0) return `${head} — no todos`;
  const names = sorted.slice(0, 4).map(t => t.title).join(", ");
  return `${head} — ${names}${sorted.length > 4 ? "…" : ""}`;
}
